import { useEffect, useRef, useState } from 'react';

import memoize from 'fast-memoize';

import { getInitialLoadIcons } from 'src/styles/font';
import { getLocalStorage } from 'src/utils/browserStorageHandler';
import { InitialLoadIconsSet } from 'src/utils/constants/IconsConstants';
import { SETTINGS } from 'src/utils/constants/localStorageConstants';
import { WINDOW_LISTNER_EVENTS } from 'src/utils/GlobalListners/WindowListnerConstants';
import useWindowEvent from 'src/utils/GlobalListners/WindowListnerHook';
import { getFeatureFlagConfig, isFeatureEnabled } from 'src/utils/RemoteConfig';

import { useAppStore } from 'src/hooks/useAppStore';
import { DynamicObject } from 'src/utils/commonTypes';
import {
  debouncedFetchData,
  getSearchSuggestion
} from '../../services/apiRequests/SearchService';
import { HTTP_HEADER_KEYS } from '../../utils/constants/HttpMethodsConstants';
import {
  CountryCode,
  DisplayLanguage
} from '../../utils/constants/stringConstants';
import {
  DEVICE_WIDTH_CONSTANTS,
  FeatureFlagConstants
} from '../../utils/constants/utilConstants';
import RecentSearch from './recentSearch/RecentSearch';
import {
  clearAllRecentSearch,
  clearRecentSearchData,
  filterWhiteSpaceAndHyphen,
  getCurrentIndex,
  saveRecentSearchLocalStorage
} from './SearchBoxHandler';
import * as style from './SearchBoxStyle';
import {
  removeAllRecentSearchMixpanelEvent,
  removeTextMixpanelEvent,
  searchCancelledMixpanelEvent,
  searchClearedMixpanelEvent,
  searchFocusMixpanelEvent,
  updateSearchStringMixpanelEvent
} from './SearchMixpanelHelper';
import SearchSuggestions from './searchSuggestions/SearchSuggestions';
import { SuggestionType } from './SearchTypes';
import VoiceSearch from './VoiceSearch';

const inMemoryCache = memoize(
  async (requestObj: DynamicObject, headers: string[]) => {
    return await getSearchSuggestion(requestObj, headers);
  }
);

type SearchBoxPropType = {
  hideMenu: boolean;
  setHideMenu: (hideMenu: boolean) => void;
};

const SearchBox = ({ hideMenu, setHideMenu }: SearchBoxPropType) => {
  const {
    appConfig,
    usersLocationData,
    usersPreferenceData,
    translations,
    setUsersPreferenceData,
    remoteConfig,
    isUserLoggedIn
  } = useAppStore();
  const headers = [HTTP_HEADER_KEYS.X_ACCESS_TOKEN];

  const shouldShowSearchSuggestions = isFeatureEnabled(
    remoteConfig,
    FeatureFlagConstants.FEATURE_SEARCH_SUGGESTION
  );

  const searchQueryLengthFromConfig = getFeatureFlagConfig(
    remoteConfig,
    FeatureFlagConstants.CONFIG_SEARCH_QUERY_LENGTH
  )?.payload;

  const settings = usersPreferenceData || getLocalStorage(SETTINGS);
  const { recent_search } = usersPreferenceData || {};
  const displayLanguage = settings?.display_language || DisplayLanguage.ENGLISH;
  const contentLanguage = settings?.content_language || DisplayLanguage.ENGLISH;
  let recentSuggestionClick = false;
  const headerStrings = translations?.common;
  const { SEARCH_TEXT } = headerStrings || {};
  const innerWidth = appConfig?.deviceWidth as number;

  const country = usersLocationData?.country_code || CountryCode.IN;
  const shouldHideNavMenu =
    country !== CountryCode.IN ||
    displayLanguage === DisplayLanguage.KANNADA ||
    displayLanguage === DisplayLanguage.MALAYALAM ||
    displayLanguage === DisplayLanguage.TAMIL;
  let languages: Array<string> = [];
  if (contentLanguage) languages = filterWhiteSpaceAndHyphen(contentLanguage);
  let recentSearchData: Array<string> = [];
  if (recent_search) {
    recentSearchData = filterWhiteSpaceAndHyphen(recent_search);
  }
  const [searchFocus, setSearchFocus] = useState(false);
  const [queryString, setQueryString] = useState('');
  const [suggestionResult, setSuggestionResult] = useState<SuggestionType[]>(
    []
  );
  const [enableSearchBox, setEnableSearchBox] = useState(false);
  const [isVoiceActive, setIsVoiceActive] = useState(false);

  const [currentIndex, setCurrentIndex] = useState(-1);
  const [showClearAllPopup, setClearAllPopup] = useState(false);

  const newSearchLandingRevamp = isFeatureEnabled(
    remoteConfig,
    FeatureFlagConstants.FEATURE_NEW_SEARCH_REVAMP_LANDING
  );
  const onFocus = () => {
    setClearAllPopup(false);
    setSearchFocus(true);
    if (shouldHideNavMenu || newSearchLandingRevamp) {
      setHideMenu(true);
    }
    if (queryString.length === 0) {
      searchFocusMixpanelEvent();
    }
  };
  const onBlur = () => {
    if (
      innerWidth >= DEVICE_WIDTH_CONSTANTS.deviceWidth1200 &&
      !showClearAllPopup
    ) {
      setSearchFocus(false);
      if (shouldHideNavMenu || newSearchLandingRevamp) {
        setEnableSearchBox(false);
        setHideMenu(false);
      }
    }
    if (!recentSuggestionClick) {
      if (!shouldShowSearchSuggestions) {
        if (queryString?.length < searchQueryLengthFromConfig) {
          searchCancelledMixpanelEvent(suggestionResult, queryString);
        }
      } else {
        searchCancelledMixpanelEvent(suggestionResult, queryString);
      }
    }
  };
  const closeIcon = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setQueryString('');
    searchClearedMixpanelEvent(queryString);
  };
  useEffect(() => {
    const requestObj = {
      country,
      languages,
      query: queryString
    };
    if (
      queryString?.length >= searchQueryLengthFromConfig &&
      currentIndex === -1
    ) {
      debouncedFetchData(
        queryString,
        setSuggestionResult,
        inMemoryCache,
        headers,
        requestObj
      );
    }
  }, [queryString]);

  const updateSearchString = (e: React.ChangeEvent<HTMLInputElement>) => {
    let searchTextTrimValue = e.target.value;
    if (isVoiceActive) {
      searchTextTrimValue = e.target.value.trim();
    }
    setQueryString(searchTextTrimValue);
    if (searchTextTrimValue.length === 1) {
      updateSearchStringMixpanelEvent(suggestionResult, searchTextTrimValue);
    }
  };

  const searchByText = (searchText: string, e: React.MouseEvent) => {
    const searchTextQuery = searchText ? searchText.replace(/,/g, '') : '';
    setQueryString(searchTextQuery);
    recentSuggestionClick = true;
    onSubmit(e, searchTextQuery);
  };

  const removeText = (currentSearch: string) => {
    const filteredSearchItem = clearRecentSearchData(
      currentSearch,
      isUserLoggedIn
    );
    if (usersPreferenceData) {
      setUsersPreferenceData({
        ...usersPreferenceData,
        recent_search: filteredSearchItem
      });
    }
    removeTextMixpanelEvent();
  };
  const clearAllRecentSearchData = () => {
    setClearAllPopup(false);
    setEnableSearchBox(false);
    setSearchFocus(false);
    clearAllRecentSearch();
    if (shouldHideNavMenu) {
      setHideMenu(false);
    }
    if (usersPreferenceData) {
      setUsersPreferenceData({
        ...usersPreferenceData,
        recent_search: ''
      });
    }
    removeAllRecentSearchMixpanelEvent();
  };

  const onSubmit = (e: React.FormEvent, queryStringValue: string) => {
    e.preventDefault();
    // TODO :: Get Search Results called but data is not used
    // const requestObject: SearchQueryInput = {
    //   country,
    //   query: queryStringValue,
    //   page: 0,
    //   limit: 24,
    //   translation: displayLanguage,
    //   languages
    // };
    // const data = getSearchResults(requestObject, headers);
    const saveRecentData = saveRecentSearchLocalStorage(
      queryStringValue,
      recentSearchData,
      newSearchLandingRevamp
    );
    if (saveRecentData && usersPreferenceData)
      setUsersPreferenceData({
        ...usersPreferenceData,
        recent_search: saveRecentData
      });
    const searchQueryUrl = `/search?q=${queryStringValue}`;
    window.location.href = searchQueryUrl;
  };

  const handleBackButtonClick = () => {
    setEnableSearchBox(false);
    setSearchFocus(false);
  };

  const voiceActiveHandler = () => {
    setIsVoiceActive(true);
  };

  const handleSearchButtonClick = (
    event: React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    event?.stopPropagation();
    searchFocusMixpanelEvent();
    setEnableSearchBox(true);
    if (!isDesktop) {
      setSearchFocus(true);
    }
    if ((isDesktop && shouldHideNavMenu) || newSearchLandingRevamp) {
      setHideMenu(true);
    }
  };

  useEffect(() => {
    if (suggestionResult.length) {
      const suggetionResultText =
        suggestionResult[currentIndex]?.text || queryString;
      setQueryString(suggetionResultText);
    }
  }, [currentIndex, suggestionResult]);

  const handleClickOutside = (event: Event) => {
    if (searchFocus && !wrapperRef?.current?.contains(event.target as Node)) {
      onBlur();
    }
  };

  useEffect(() => {
    if (searchFocus) {
      onBlur();
    }
  }, [innerWidth]);

  const wrapperRef = useRef<HTMLDivElement>(null);

  useWindowEvent(WINDOW_LISTNER_EVENTS.CLICK, handleClickOutside);

  const displayRecentSearch = () => {
    if (recentSearchData && searchFocus && !queryString?.length) {
      return (
        <RecentSearch
          recentSearch={recentSearchData}
          removeText={removeText}
          clearAllRecentSearchData={clearAllRecentSearchData}
          searchByText={searchByText}
          showClearAllPopup={showClearAllPopup}
          setClearAllPopup={setClearAllPopup}
          setSearchFocus={setSearchFocus}
          hideMenu={hideMenu}
          onBlur={onBlur}
          setHideMenu={setHideMenu}
          newSearchLandingRevamp={newSearchLandingRevamp}
        />
      );
    }
  };
  const isDesktop = innerWidth >= DEVICE_WIDTH_CONSTANTS.deviceWidth1200;

  return enableSearchBox || (isDesktop && !shouldHideNavMenu) ? (
    <div
      css={style.mainSearchWrapper(searchFocus)}
      data-testid="search-wrapper">
      {isDesktop && searchFocus ? <div css={style.searchOverly}></div> : null}
      <style.SearchWrapper ref={wrapperRef}>
        <style.SearchContainer data-testid="search-box-container">
          {enableSearchBox && !isDesktop ? (
            <div css={style.backArrowCss} onClick={handleBackButtonClick} />
          ) : null}
          <div
            css={style.searchBoxCss(
              searchFocus,
              hideMenu,
              newSearchLandingRevamp
            )}>
            <style.SearchIconCss />
            <style.SearchInputBox>
              <form
                autoComplete="off"
                onSubmit={(e) => onSubmit(e, queryString)}>
                <input
                  data-testid="search-input"
                  type="text"
                  name="search"
                  id="searchInput"
                  placeholder={SEARCH_TEXT}
                  onChange={updateSearchString}
                  onFocus={onFocus}
                  autoFocus={shouldHideNavMenu || !isDesktop ? true : false}
                  spellCheck="false"
                  autoComplete="off"
                  autoCorrect="off"
                  value={queryString}
                  onKeyUp={(e) => {
                    getCurrentIndex(
                      e,
                      suggestionResult,
                      currentIndex,
                      setCurrentIndex
                    );
                  }}
                />
              </form>
            </style.SearchInputBox>
            <style.MicOrCloseIcon>
              {queryString.length ? (
                <span
                  css={style.closeIconCss}
                  onClick={closeIcon}
                  data-testid="close-icon"
                />
              ) : searchFocus ? (
                <span
                  css={style.micIconCss}
                  onClick={voiceActiveHandler}
                  data-testid="mic-icon"
                />
              ) : (
                ''
              )}
            </style.MicOrCloseIcon>
          </div>
        </style.SearchContainer>
        {displayRecentSearch()}

        {shouldShowSearchSuggestions &&
        suggestionResult &&
        suggestionResult.length > 0 &&
        queryString.length >= searchQueryLengthFromConfig &&
        searchFocus ? (
          <SearchSuggestions
            searchSuggestions={suggestionResult}
            currentIndex={currentIndex}
            queryString={queryString}
            searchByText={searchByText}
          />
        ) : (
          ''
        )}
        {isVoiceActive ? (
          <VoiceSearch
            setIsVoiceActive={setIsVoiceActive}
            setQueryString={setQueryString}
          />
        ) : null}
      </style.SearchWrapper>
    </div>
  ) : (
    <style.SearchIcon
      css={getInitialLoadIcons(InitialLoadIconsSet.SEARCH)}
      onClick={handleSearchButtonClick}
      data-testid="search-icon"></style.SearchIcon>
  );
};

export default SearchBox;
